import { useCallback, useRef, useState, Reducer } from "preact/hooks";

export interface ThunkDispatch<S, A> {
  <
    Action extends (dispatch: ThunkDispatch<S, A>, getState: () => S) => unknown
  >(
    action: Action
  ): ReturnType<Action>;
  (value: A): void;
}

function useThunkReducer<S, A>(
  reducer: Reducer<S, A>,
  initialArg: S,
  init: (s: S) => S = (a) => a
): [S, ThunkDispatch<S, A>] {
  const [hookState, setHookState] = useState(init(initialArg));

  // State management.
  const state = useRef(hookState);
  const getState = useCallback(() => state.current, [state]);
  const setState = useCallback(
    (newState: S) => {
      state.current = newState;
      setHookState(newState);
    },
    [state, setHookState]
  );

  // Reducer and augmented dispatcher.
  const reduce = useCallback(
    (action: A) => reducer(getState(), action),
    [reducer, getState]
  );
  const dispatch = useCallback(
    (action: A) =>
      typeof action === "function"
        ? action(dispatch, getState)
        : setState(reduce(action)),
    [getState, setState, reduce]
  );

  return [hookState, dispatch];
}

export default useThunkReducer;
